import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthStore } from "~/store/authentication/useAuthStore";
import { axiosPrivate } from "~/config/axios.config";
import { ENV } from "~/config/env.config";
import { logError } from "~/lib/logger";
import { Shield } from "lucide-react";

/**
 * OPS-3.13 — ends the session the calling impersonation token belongs to.
 *
 * Relative to axiosPrivate's baseURL, which (VITE_API_BASE_URL) already ends
 * in `/api` — every service in this app calls `/v1/...`, never `/api/v1/...`.
 * This used to be `/api/internal/impersonation/self/end`, which went out as
 * `/api/api/internal/...`, 404'd, and was swallowed by the catch below: Exit
 * redirected to the Ops Panel while the session stayed live server-side
 * until its 2-hour expiry.
 *
 * `/self/end` accepts the impersonation token directly. The parametric
 * `/:sessionId/end` is gated by InternalAuthGuard (type=internal), which the
 * admin app never holds during impersonation.
 */
export const IMPERSONATION_SELF_END_PATH = "/internal/impersonation/self/end";

// ---------------------------------------------------------------------------
// JWT helpers — decode the payload without adding a library dependency.
//
// The backend nests session-specific claims under `impersonation` to mirror
// the structure documented in internal-ops-panel-scope.md. `type` and `iat`
// stay top-level because they're JWT-standard / cross-cutting.
// ---------------------------------------------------------------------------

interface ImpersonationClaims {
  type?: string;
  iat?: number;
  impersonation?: {
    sessionId?: string;
    organizationId?: string;
    grantedRole?: string;
    restaurantName?: string;
  };
}

function decodeJwtPayload(token: string): ImpersonationClaims | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // Base64url → binary string → UTF-8 decode
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json) as ImpersonationClaims;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Timer — formats elapsed seconds as HH:MM:SS or MM:SS
// ---------------------------------------------------------------------------

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  if (h > 0) return `${String(h).padStart(2, "0")}:${mm}:${ss}`;
  return `${mm}:${ss}`;
}

// ---------------------------------------------------------------------------
// CSS offset — layouts that use h-screen read this variable to shrink by the
// banner height. When no impersonation is active the variable is absent and
// var(--impersonation-offset, 0px) resolves to 0px → zero visual impact.
// ---------------------------------------------------------------------------

const BANNER_HEIGHT_PX = 48;

function applyOffset() {
  document.documentElement.style.setProperty(
    "--impersonation-offset",
    `${BANNER_HEIGHT_PX}px`,
  );
  document.body.style.paddingTop = `${BANNER_HEIGHT_PX}px`;
}

function removeOffset() {
  document.documentElement.style.removeProperty("--impersonation-offset");
  document.body.style.paddingTop = "";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ImpersonationBanner() {
  const token = useAuthStore((s) => s.token);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const [claims, setClaims] = useState<ImpersonationClaims | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [isEnding, setIsEnding] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Decode token whenever it changes
  useEffect(() => {
    if (!token) {
      setClaims(null);
      return;
    }
    const decoded = decodeJwtPayload(token);
    if (decoded?.type === "impersonation") {
      setClaims(decoded);
      // Initialise elapsed from iat
      if (decoded.iat) {
        const now = Math.floor(Date.now() / 1000);
        setElapsed(Math.max(0, now - decoded.iat));
      } else {
        setElapsed(0);
      }
    } else {
      setClaims(null);
    }
  }, [token]);

  // Apply / remove the CSS offset when impersonation state changes
  useEffect(() => {
    if (claims) {
      applyOffset();
    } else {
      removeOffset();
    }
    return () => removeOffset();
  }, [claims]);

  // Tick the timer every second while impersonating
  useEffect(() => {
    if (!claims) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [claims]);

  const sessionId = claims?.impersonation?.sessionId;

  const handleExit = useCallback(async () => {
    if (!sessionId || isEnding) return;
    setIsEnding(true);
    try {
      await axiosPrivate.post(IMPERSONATION_SELF_END_PATH);
    } catch (error) {
      // Still leave support mode locally and go back to the Ops Panel — the
      // agent asked to exit, and staying signed in as the restaurant is the
      // worse failure. But the server-side session may still be live (it
      // expires on its own), so this must be visible, not swallowed.
      logError("Ending the impersonation session failed", error, {
        category: "impersonation",
        tags: { error_type: "impersonation_self_end_failed" },
        data: { sessionId },
      });
    } finally {
      clearAuth();
      const opsUrl = ENV.OPS_PANEL_URL || "/ops";
      window.location.href = opsUrl;
    }
  }, [sessionId, isEnding, clearAuth]);

  // Normal user — render nothing
  if (!claims) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] flex h-12 items-center justify-between bg-amber-500 px-2 sm:px-4 text-xs sm:text-sm font-medium text-white shadow-md select-none"
      role="status"
      aria-live="polite"
    >
      <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
        <Shield className="h-4 w-4 shrink-0" />
        <span className="truncate">
          <span className="hidden sm:inline">You are viewing as </span>
          <span className="sm:hidden">Viewing </span>
          <span className="font-semibold">
            {claims.impersonation?.restaurantName ?? "Unknown restaurant"}
          </span>
          <span className="hidden md:inline"> — Support mode</span>
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2 sm:gap-4">
        <span className="tabular-nums font-mono text-amber-100">
          {formatElapsed(elapsed)}
        </span>
        <button
          type="button"
          onClick={handleExit}
          disabled={isEnding}
          className="whitespace-nowrap rounded-md bg-white/20 px-2 py-1 text-xs font-semibold text-white transition-colors hover:bg-white/30 disabled:opacity-50 sm:px-3 sm:text-sm"
        >
          {isEnding ? "Exiting…" : "Exit"}
          <span className="hidden sm:inline"> Support mode</span>
        </button>
      </div>
    </div>
  );
}
